import { supabase } from "@/integrations/supabase/client";

export type LiveVisitorRow = {
  visitor_id: string;
  page: string;
  device: string;
  browser: string;
  os: string;
  last_seen: string;
  created_at: string;
};

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}`.replace(
  ".supabase.co",
  ".functions.supabase.co"
);
const HEARTBEAT_URL = `${FUNCTIONS_URL}/functions/v1/presence-heartbeat`;

async function restUpsertHeartbeat(input: {
  visitorId: string;
  page: string;
  device: string;
  browser: string;
  os: string;
}) {
  // Use backend function to avoid any CORS / header quirks on custom domains.
  const body = {
    visitor_id: input.visitorId,
    page: input.page,
    device: input.device,
    browser: input.browser,
    os: input.os,
  };

  const res = await fetch(HEARTBEAT_URL, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(body),
    // Helps Safari keep the request alive during page transitions
    keepalive: true,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Heartbeat function failed: ${res.status} ${text}`);
  }
}

export function sendBeaconLiveVisitorHeartbeat(input: {
  visitorId: string;
  page: string;
  device: string;
  browser: string;
  os: string;
}) {
  try {
    if (typeof navigator === "undefined" || !("sendBeacon" in navigator)) return false;

    const body = {
      visitor_id: input.visitorId,
      page: input.page,
      device: input.device,
      browser: input.browser,
      os: input.os,
    };

    const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
    return navigator.sendBeacon(HEARTBEAT_URL, blob);
  } catch {
    return false;
  }
}

export async function upsertLiveVisitorHeartbeat(input: {
  visitorId: string;
  page: string;
  device: string;
  browser: string;
  os: string;
}) {
  // Always prefer the backend function. It's the most reliable across custom domains and mobile browsers.
  try {
    await restUpsertHeartbeat(input);
    return { data: null, error: null } as any;
  } catch (e) {
    console.error("[Presence] Heartbeat function failed, trying direct DB upsert", e);
    const res = await supabase.from("live_visitors").upsert(
      {
        visitor_id: input.visitorId,
        page: input.page,
        device: input.device,
        browser: input.browser,
        os: input.os,
        last_seen: new Date().toISOString(),
      },
      { onConflict: "visitor_id" }
    );
    if (res.error) {
      console.error("[Presence] Direct DB upsert failed", res.error);
    }
    return res;
  }
}

export async function fetchLiveVisitorsActiveWithin(seconds = 180) {
  const sinceIso = new Date(Date.now() - seconds * 1000).toISOString();
  return supabase
    .from("live_visitors")
    .select("visitor_id,page,device,browser,os,last_seen,created_at")
    .gte("last_seen", sinceIso)
    .order("last_seen", { ascending: false });
}
